import { useCallback, useEffect, useRef, useState } from 'react'

const useRepeatableAnimation = (
    enabled: boolean,                 // extra flag to turn timers ON/OFF
    inactivityDelay = 10000,          // inactivity delay
    animationCycleDuration = 15000      // animation duration
) => {
    const [isAnimating, setIsAnimating] = useState(false)
    const inactivityTimer = useRef<NodeJS.Timeout>()
    const animationTimer = useRef<NodeJS.Timeout>()

    // Track when animation cycle started/ended for logging
    const startTimestamp = useRef<number>(Date.now())

    const clearTimers = useCallback(() => {
        if (inactivityTimer.current) clearTimeout(inactivityTimer.current)
        if (animationTimer.current) clearTimeout(animationTimer.current)
    }, [])

    const startCycle = useCallback(() => {
        // Log time when animation starts
        const elapsed = (Date.now() - startTimestamp.current) / 1000
        console.log(`Starting animation after ${elapsed.toFixed(2)} seconds.`)

        setIsAnimating(true)
        animationTimer.current = setTimeout(() => {
            // Log time when animation ends
            const animationElapsed = (Date.now() - startTimestamp.current) / 1000
            console.log(`Animation ended after ${animationElapsed.toFixed(2)} seconds.`)

            setIsAnimating(false)
            if (enabled) startInactivityTimer()
        }, animationCycleDuration)
    }, [animationCycleDuration, enabled])

    const startInactivityTimer = useCallback(() => {
        clearTimers()
        inactivityTimer.current = setTimeout(() => {
            startCycle()
        }, inactivityDelay)
    }, [inactivityDelay])

    // Listen for global clicks to reset timers
    useEffect(() => {
        if (!enabled) return
        const handleGlobalClick = () => {
            reset()
        }
        document.addEventListener('mousedown', handleGlobalClick)
        return () => {
            document.removeEventListener('mousedown', handleGlobalClick)
        }
    }, [enabled])

    const reset = useCallback(() => {
        if (enabled) {
            console.log('Global click detected. Resetting timers.')
            startTimestamp.current = Date.now()
            setIsAnimating(false)
            startInactivityTimer()
        }
    }, [enabled, startInactivityTimer])

    useEffect(() => {
        if (enabled) {
            startInactivityTimer()
        } else {
            clearTimers()
            setIsAnimating(false)
        }
        return () => {
            clearTimers()
        }
    }, [enabled])

    return { isAnimating, reset: startInactivityTimer }
}

export default useRepeatableAnimation